import { ComponentType, useCallback, useEffect, useRef, useState } from 'react';
import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosResponse } from 'axios';
import { useRouter } from 'next/router';

import { useAppDispatch, useAppSelector, useLoader, useOnboardingTask, useTracking } from '@/hooks';
import { authThunk, setAdminLogin, setRedirectTo } from '@/redux/features/authSlice';
import { AuthorizationResponse, QueryParams } from '@/types/auth.type';

import tokenStorageService from '../../utils/token.util';

import {
  getAuthorizationPayloadFromCatch,
  isClientOrServerError,
  isSuccess,
  isUnauthorized,
  isValidToken,
  SKIP_AUTH_REDIRECT,
} from './authGuard.utils';
import { ErrorEventFeedback } from './ErrorEventFeedback';

export const withAuthGuard = <P extends Record<string, unknown>>(Component: ComponentType<P>) => (
  function AuthGuard(props: P) {
    const router = useRouter();
    const dispatch = useAppDispatch();
    const { showLoader, hideLoader } = useLoader();
    const { redirectToLogin } = useOnboardingTask();
    const { trackStepChangePage } = useTracking();
    const company = useAppSelector(state => state.auth.company);
    const redirectTo = useAppSelector(state => state.auth.redirectTo);
    const [status, setStatus] = useState<'loading' | 'error' | 'completed'>('loading');
    const didFirstLoad = useRef(false);

    const trackAuth = useCallback(
      async () => trackStepChangePage('Auth', {}),
      [trackStepChangePage],
    );

    const handleAuthSuccess = useCallback(async () => {
      await dispatch(authThunk.freeEmailDomain());
      if (company) await trackAuth();
      await router.push(router.pathname);
      setStatus('completed');
    }, [company, dispatch, router, trackAuth]);

    const handleAuthError = useCallback(
      (payloadStatus?: number) => {
        if (isUnauthorized(payloadStatus)) {
          if (SKIP_AUTH_REDIRECT) {
            setStatus('error');
          } else {
            redirectToLogin(redirectTo);
          }
          return true;
        }
        if (isClientOrServerError(payloadStatus)) {
          setStatus('error');
          return true;
        }
        return false;
      },
      [redirectToLogin, redirectTo],
    );

    const authenticate = useCallback(
      async (token: string) => {
        const { payload } =
          (await dispatch(authThunk.authorization(token))) as PayloadAction<AxiosResponse<AuthorizationResponse>>;

        if (handleAuthError(payload?.status)) return;

        await handleAuthSuccess();
      },
      [dispatch, handleAuthError, handleAuthSuccess],
    );

    /**
     * When no token in URL: try cookie-based auth first (V2 only), then fall back to stored token.
     */
    const checkStoredTokenOrCookie = useCallback(async () => {
      let payload: AxiosResponse<AuthorizationResponse> | undefined;
      try {
        payload = await dispatch(authThunk.authorizationWithCookies()).unwrap();
      } catch (reason: unknown) {
        payload = getAuthorizationPayloadFromCatch(reason);
      }

      if (payload && isSuccess(payload.status)) {
        tokenStorageService.removeToken();
        await handleAuthSuccess();
        return;
      }

      const storedToken = tokenStorageService.getToken();
      if (isValidToken(storedToken)) {
        await authenticate(storedToken);
        return;
      }

      if (handleAuthError(payload?.status)) return;

      setStatus('error');
    }, [authenticate, dispatch, handleAuthError, handleAuthSuccess]);

    const checkQueryToken = useCallback(async () => {
      const { token, adminLogin, redirectTo: redirectToParam } = router.query as QueryParams;

      if (!isValidToken(token)) {
        dispatch(setAdminLogin(adminLogin === '1'));
        dispatch(setRedirectTo(redirectToParam));
        return checkStoredTokenOrCookie();
      }

      tokenStorageService.setToken(token);
      dispatch(setAdminLogin(adminLogin === '1'));
      dispatch(setRedirectTo(redirectToParam));
      await authenticate(token);
    }, [authenticate, checkStoredTokenOrCookie, dispatch, router]);

    useEffect(() => {
      if (didFirstLoad.current) return;

      showLoader();

      const hasTokenInQuery = Object.keys(router.query).length > 0 && router.query.token != null;
      const isReadyWithoutToken = router.isReady && !router.query?.token;

      if (hasTokenInQuery) {
        checkQueryToken();
        didFirstLoad.current = true;
      } else if (isReadyWithoutToken) {
        checkStoredTokenOrCookie();
        didFirstLoad.current = true;
      }
    }, [router, dispatch, checkQueryToken, checkStoredTokenOrCookie, showLoader]);

    useEffect(() => {
      if (status === 'error') hideLoader();
    }, [status, hideLoader]);

    if (status === 'loading') return null;
    if (status === 'error') return <ErrorEventFeedback />;

    return <Component { ...props } />;
  }
);
